import React, { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from 'emailjs-com';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import heroBg from '../assets/images/sl_122221_47450_06.jpg';

gsap.registerPlugin(ScrollTrigger);

const wrongChoices = [
  { icon: '💬', label: '"נשמע טוב"' },
  { icon: '👥', label: 'כמו שחברים עושים' },
  { icon: '🤷', label: 'תחושת בטן' },
];

const questions = [
  'מה באמת מתאים לי?',
  'מה יש לי סיכוי אמיתי להצליח בו?',
  'ומה פשוט לא שווה את הזמן שלי?',
];

const learnDimensions = [
  'מה עושים ביום-יום',
  'כישורים נדרשים',
  'רקע לימודי',
  'שכר ריאלי בישראל',
  'התקדמות בקריירה',
  'מצב הביקוש + AI',
];

const domains = [
  {
    title: 'Web, Product & Infra',
    subtitle: 'פיתוח, מוצר ותשתיות',
    desc: 'הקטגוריה הרחבה ביותר, נסקר את כל התפקידים שבונים מוצר מהקוד ועד המשתמש.',
    roles: ['Full Stack Developer', 'Frontend Developer', 'Backend Developer', 'DevOps Engineer', 'QA Engineer', 'Product Manager', 'UX/UI Designer', 'Cloud Architect'],
  },
  {
    title: 'AI & Machine Learning',
    subtitle: 'בינה מלאכותית ולמידת מכונה',
    desc: 'התחום שמשנה את כל שאר התחומים. נבין מה עושים בפועל, מה ההבדל בין תפקידי AI שונים, ואיפה שוק העבודה הישראלי עומד היום.',
    roles: ['AI Engineer', 'Machine Learning Engineer', 'Data Scientist'],
  },
  {
    title: 'Data',
    subtitle: 'נתונים ואנליטיקה',
    desc: 'עולם הדאטה מתרחב בקצב מהיר. נבין מה ההבדל בין מי שמנתח נתונים לבין מי שבונה את הצנרת שמביאה אותם ואיזה כישורים נדרש לכל תפקיד.',
    roles: ['Data Analyst', 'Data Engineer'],
  },
  {
    title: 'Embedded',
    subtitle: 'פיתוח משובץ וקושחה',
    desc: 'הצד הפחות מוכר של ההייטק — אבל קריטי. נסקר מה עושים מפתחי Embedded ו-Firmware בפועל, באילו תעשיות הם עובדים בישראל, ומה ההבדל מפיתוח תוכנה רגיל.',
    roles: ['Embedded Developer', 'Firmware Engineer'],
  },
  {
    title: 'Cyber Security',
    subtitle: 'אבטחת מידע וסייבר',
    desc: 'תחום שבו ישראל היא מעצמה עולמית. נסקר את הכיוונים השונים מהגנה עד התקפה ומה נדרש כדי להיכנס לכל אחד מהם, כולל מסלולים ללא תואר.',
    roles: ['SOC Analyst', 'מיישם אבטחת מידע', 'Penetration Tester', 'Security Researcher'],
  },
  {
    title: 'IT',
    subtitle: 'תשתיות ורשתות',
    desc: 'תפקידים שכל ארגון גם מחוץ להייטק צריך. נבין מה עושים בפועל, מה מסלולי הצמיחה מ-IT קלאסי לתפקידים טכנולוגיים מתקדמים יותר, ומה השכר הריאלי.',
    roles: ['Network Engineer', 'System Administrator'],
  },
];

// const forWho = [
//   { icon: '🔍', text: 'לא בטוח מה ללמוד או באיזה כיוון לבחור' },
//   { icon: '🌀', text: 'מרגיש שיש יותר מדי אפשרויות ואין לך בהירות' },
//   { icon: '🔄', text: 'כבר לומד/עובד אבל לא בטוח שזה המסלול הנכון' },
//   { icon: '🏗️', text: 'רוצה להבין את שוק ההייטק בצורה אמיתית ולא תיאורטית' },
// ];

const bonuses = [
  { icon: '🧭', title: 'שאלון אישי', desc: 'שמתרגם את התשובות שלך ל-3 כיווני קריירה מדויקים עבורך' },
  { icon: '🧠', title: 'שיטת קבלת החלטות', desc: 'כלי פרקטי לבחירה שקולה ולא אימפולסיבית' },
  { icon: '🎁', title: 'הנחות על ייעוץ אישי', desc: 'מחירים מיוחדים להמשך הדרך ביחד איתי' },
];

const stats = [
  { num: '21', label: 'תפקידים טכנולוגיים' },
  { num: '5+', label: 'שעות תוכן' },
  { num: '∞', label: 'גישה לתמיד' },
];

const priceIncludes = [
  '21 תפקידים טכנולוגיים מפורטים',
  'מעל 5 שעות תוכן מעמיק',
  'שכר ריאלי · AI impact · מסלולי קריירה',
  'שיטת קבלת החלטות קריירה',
  'גישה לצמיתות + עדכונים',
  'הנחות על ייעוץ אישי להמשך',
];

function useFadeUp(ref, selector, options = {}) {
  useGSAP(() => {
    const els = ref.current ? ref.current.querySelectorAll(selector) : [];
    if (!els.length) return;
    gsap.set(els, { autoAlpha: 0, y: options.y ?? 40 });
    gsap.to(els, {
      autoAlpha: 1,
      y: 0,
      duration: options.duration ?? 0.7,
      stagger: options.stagger ?? 0.1,
      ease: options.ease ?? 'power2.out',
      scrollTrigger: {
        trigger: ref.current,
        start: options.start ?? 'top 88%',
        once: true,
      },
    });
  }, { scope: ref });
}

const CoursePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', tel: '' });
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleFormChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.tel || !formData.email) {
      setFormError('נא למלא שם, טלפון ואימייל.');
      return;
    }

    setFormError('');
    setSending(true);

    emailjs
      .sendForm('service_8vockx8', 'template_1jxjmsh', e.target, 'jNZT47WVzJyjAHFSA')
      .then(
        () => {
          setSending(false);
          setSubmitted(true);
        },
        (error) => {
          console.error('Error:', error.text);
          setSending(false);
          setFormError('שליחת הטופס נכשלה, נסו שוב.');
        }
      );
  };

  const heroRef = useRef(null);
  const problemRef = useRef(null);
  const questionsRef = useRef(null);
  const shovalRef = useRef(null);
  const learnRef = useRef(null);
  const aiRef = useRef(null);
  // const forWhoRef = useRef(null);
  const bonusRef = useRef(null);
  const closingRef = useRef(null);
  const pricingRef = useRef(null);

  // Hero entrance
  useGSAP(() => {
    const tl = gsap.timeline({ delay: 0.3 });
    tl.from('.hero-badge', { scale: 0, autoAlpha: 0, duration: 0.5, ease: 'back.out(1.7)' })
      .from('.hero-title', { y: 60, autoAlpha: 0, duration: 1, ease: 'power3.out' }, '-=0.2')
      .from('.hero-sub', { y: 30, autoAlpha: 0, duration: 0.7, ease: 'power2.out' }, '-=0.5')
      .from('.hero-cta a', { y: 20, autoAlpha: 0, stagger: 0.15, duration: 0.5, ease: 'back.out(1.4)' }, '-=0.3');
  }, { scope: heroRef });

  // Problem section
  useFadeUp(problemRef, '.wrong-card', { stagger: 0.12, y: 30 });

  // Questions
  useGSAP(() => {
    const els = questionsRef.current?.querySelectorAll('.q-card');
    if (!els?.length) return;
    gsap.set(els, { autoAlpha: 0, x: 40 });
    gsap.to(els, {
      autoAlpha: 1, x: 0, duration: 0.6, stagger: 0.15, ease: 'power3.out',
      scrollTrigger: { trigger: questionsRef.current, start: 'top 88%', once: true },
    });
  }, { scope: questionsRef });

  // Shoval credentials
  useFadeUp(shovalRef, '.cred-item', { stagger: 0.1 });

  // What you learn cards   explicit set then to
  useGSAP(() => {
    const cards = learnRef.current?.querySelectorAll('.learn-card');
    if (!cards?.length) return;
    gsap.set(cards, { autoAlpha: 0, y: 50 });
    ScrollTrigger.create({
      trigger: learnRef.current,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(cards, {
          autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.09, ease: 'power2.out',
        });
      },
    });
  }, { scope: learnRef });

  // For who
  // useGSAP(() => {
  //   const els = forWhoRef.current?.querySelectorAll('.forwho-card');
  //   if (!els?.length) return;
  //   gsap.set(els, { autoAlpha: 0, x: -30 });
  //   gsap.to(els, {
  //     autoAlpha: 1, x: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out',
  //     scrollTrigger: { trigger: forWhoRef.current, start: 'top 88%', once: true },
  //   });
  // }, { scope: forWhoRef });

  // Bonuses
  useFadeUp(bonusRef, '.bonus-card', { stagger: 0.12 });

  // AI section
  useFadeUp(aiRef, '.ai-block', { stagger: 0.15, y: 30 });

  // Closing
  useFadeUp(closingRef, '.closing-line', { stagger: 0.15, y: 25 });

  // Pricing
  useGSAP(() => {
    const card = pricingRef.current?.querySelector('.pricing-card');
    if (!card) return;
    gsap.set(card, { autoAlpha: 0, y: 40, scale: 0.97 });
    ScrollTrigger.create({
      trigger: pricingRef.current,
      start: 'top 88%',
      once: true,
      onEnter: () => gsap.to(card, { autoAlpha: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out' }),
    });
  }, { scope: pricingRef });

  return (
    <div className="bg-white min-h-screen" dir="rtl">
      <Navbar />

      {/* English product description for payment processor review */}
      <div className="sr-only" aria-hidden="false" dir="ltr">
        <h1>Tech Career Course — Self-Paced Digital Educational Product</h1>
        <p>
          A self-paced digital course covering 21 technology career paths in Israel.
          Includes pre-recorded content, written guides, salary data, and career assessment tools.
          Price: ₪49 one-time payment. No personal coaching or consulting services included.
          Sold by Shoval Saada (sole proprietor). All content delivered digitally.
        </p>
      </div>

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center text-white text-center px-6 pt-24 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/30" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <span className="hero-badge inline-block bg-white/15 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2 rounded-full mb-8 border border-white/25">
            🎓 קורס דיגיטלי · השקה מיוחדת
          </span>
          <h1 className="hero-title text-5xl md:text-7xl font-extrabold mb-4 leading-tight">
            קריירה טכנולוגית
          </h1>
          <p className="hero-title text-2xl md:text-3xl font-semibold mb-6 text-white/80">
            המדריך האמיתי לאיך לבחור קריירה
          </p>
          <p className="hero-sub text-lg md:text-l text-white/65 max-w-2xl mx-auto leading-relaxed mb-10">
            תפקידים טכנולוגיים · שכר ריאלי · השפעת AI · ביקוש בשוק · תנאי קבלה אמיתיים
          </p>
          <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#pricing"
              className="px-10 py-4 bg-white text-purple-800 font-bold rounded-full text-xl hover:bg-pink-50 transition-colors duration-200 shadow-xl"
            >
              הרשמה לקורס   ₪49
            </a>
            <a
              href="#learn"
              className="px-10 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-full text-xl hover:bg-white/20 transition-colors duration-200"
            >
              תכנית הקורס
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="bg-gray-900 text-white py-10 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 text-center">
          {stats.map((s, i) => (
            <div key={i}>
              <div className="text-4xl font-extrabold text-pink-400">{s.num}</div>
              <div className="text-gray-400 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PROBLEM ── */}
      <section ref={problemRef} className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            האם אתם באמת יודעים מה ללמוד כדי לבנות קריירה בהייטק?
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-10">
            רוב האנשים בוחרים מסלול לימודים מתוך חוסר ודאות. הם בוחרים לפי...
          </p>

          {/* Wrong choice cards */}
          <div className="grid grid-cols-3 gap-4 mb-12">
            {wrongChoices.map((c, i) => (
              <div
                key={i}
                className="wrong-card bg-gray-50 border border-gray-200 rounded-2xl p-5 text-center"
              >
                <div className="text-4xl mb-3">{c.icon}</div>
                <p className="font-semibold text-gray-700">{c.label}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-600 text-lg leading-relaxed mb-4">
            ואז, כמה שנים קדימה אתם מגלים שהבחירה לא מתאימה לכם   או שלא מבינים לאן היא מובילה.
          </p>
          <div className="bg-amber-50 border-r-4 border-amber-400 rounded-xl p-5 text-right my-8">
            <p className="text-amber-800 font-semibold text-lg">הבעיה היא לא בכם.</p>
            <p className="text-amber-700 mt-1">הבעיה היא בעולם העבודה עצמו   שמשתנה מהר יותר מאי פעם.</p>
          </div>
          <p className="text-gray-600 text-lg leading-relaxed">
            תפקידים נעלמים, תפקידים חדשים נוצרים, ועם כניסת AI הגבולות בין מקצועות מטשטשים לחלוטין.
            מה שנחשב "בטוח ללמוד" לפני כמה שנים   <strong>לא בהכרח רלוונטי היום.</strong>
          </p>
        </div>
      </section>

      {/* ── THE REAL QUESTIONS ── */}
      <section ref={questionsRef} className="py-16 px-6 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-gray-500 font-medium mb-4 text-lg">במציאות כזו, השאלה החשובה כבר לא רק "מה ללמוד", אלא:</p>
          <div className="space-y-4">
            {questions.map((q, i) => (
              <div
                key={i}
                className="q-card bg-white rounded-2xl px-8 py-5 shadow-md border border-purple-100 text-right flex items-center gap-4"
              >
                <span className="w-9 h-9 rounded-full bg-purple-100 text-purple-600 font-bold flex items-center justify-center shrink-0 text-lg">
                  {i + 1}
                </span>
                <p className="text-gray-800 font-semibold text-xl">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SHOVAL ── */}
      <section ref={shovalRef} className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">נעים להכיר – אני שובל</h2>
          <p className="text-gray-500 mb-10">מגייסת ויועצת קריירה</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              { num: '6+', label: 'שנות ניסיון בחברות גיוס', icon: '💼' },
              { num: '1000+', label: 'מועמדים שליוויתי', icon: '👥' },
              { num: '100+', label: 'תהליכי גיוס', icon: '🎯' },
            ].map((c, i) => (
              <div key={i} className="cred-item bg-purple-50 rounded-2xl p-6 border border-purple-100">
                <div className="text-3xl mb-2">{c.icon}</div>
                <div className="text-2xl font-extrabold text-purple-700">{c.num}</div>
                <div className="text-gray-500 text-sm mt-1">{c.label}</div>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 rounded-2xl p-6 text-right border border-gray-100">
            <p className="text-gray-700 text-lg leading-relaxed">
              עבדתי בתוך ארגונים, דרך חברות השמה ובמודלים של גיוס אאוטסורסינג.
              נחשפתי לשיחות יומיומיות עם מנהלים שמקבלים החלטות בפועל.
            </p>
            <p className="text-gray-800 font-bold mt-4 text-lg">
              מה שראיתי שוב ושוב זה פער בין איך שתפקידים <em>נראים מבחוץ</em> לבין מה שקורה <em>באמת בשטח</em>.
              ולפער הזה יש מחיר: זמן, כסף ותסכול.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT YOU LEARN ── */}
      <section id="learn" ref={learnRef} className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-800 mb-3">זה מה שתלמדו עבור כל אחד מהתפקידים הכי נחשקים</h2>
            <p className="text-gray-500 text-lg mb-8">בלי סיסמאות. בלי רעש. בלי מידע כללי מדי.</p>
            <div className="flex flex-wrap justify-center gap-2 mb-2">
              {learnDimensions.map((d, i) => (
                <span key={i} className="bg-purple-100 text-purple-700 text-sm font-medium px-4 py-1.5 rounded-full">
                  {d}
                </span>
              ))}
            </div>
            <p className="text-gray-400 text-sm mt-3">על כל אחד מהתפקידים בכל קטגוריה</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((domain, i) => (
              <div
                key={i}
                className="learn-card bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col"
              >
                <div className="mb-4">
                  <h3 className="text-xl font-extrabold text-gray-900 mb-0.5">{domain.title}</h3>
                  <p className="text-purple-600 text-sm font-medium">{domain.subtitle}</p>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-1">{domain.desc}</p>
                <ul className="space-y-1.5 border-t border-gray-100 pt-4">
                  {domain.roles.map((role, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-700 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                      {role}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI SECTION ── */}
      <section ref={aiRef} className="py-20 px-6 bg-indigo-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="ai-block text-3xl md:text-4xl font-bold mb-6">
            ומה קורה כשמכניסים לתוך זה את עולם ה-AI?
          </h2>
          <p className="ai-block text-white/70 text-lg leading-relaxed mb-10">
            אי אפשר להתעלם מזה יותר. ה-AI משנה את שוק העבודה בצורה עמוקה.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: '📉', title: 'מצטמצם', desc: 'תפקידים שהופכים לפחות רלוונטיים' },
              { icon: '🔄', title: 'משתנה', desc: 'תפקידים שמתייעלים ומשנים צורה' },
              { icon: '🚀', title: 'נוצר מחדש', desc: 'הזדמנויות חדשות שלא היו קודם' },
            ].map((b, i) => (
              <div key={i} className="ai-block bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/15">
                <div className="text-4xl mb-3">{b.icon}</div>
                <h3 className="font-bold text-lg mb-2">{b.title}</h3>
                <p className="text-white/65 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
          <p className="ai-block mt-10 text-white/80 text-lg font-medium">
            מי שמבין מה באמת שווה ללמוד היום   <span className="text-pink-300 font-bold">מקבל יתרון משמעותי לעתיד שלו.</span>
          </p>
        </div>
      </section>

      {/* ── FOR WHO ── */}
      {/* <section ref={forWhoRef} className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-3 text-center">הקורס מתאים לך אם</h2>
          <p className="text-gray-500 text-center mb-10">הקורס פונה לכולם   אבל הוא הכי מתאים לך אם:</p>
          <div className="space-y-4">
            {forWho.map((item, i) => (
              <div
                key={i}
                className="forwho-card flex items-center gap-4 bg-gradient-to-l from-purple-50 to-white rounded-2xl px-6 py-5 border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all duration-200"
              >
                <span className="text-3xl shrink-0">{item.icon}</span>
                <span className="text-gray-700 text-lg font-medium">{item.text}</span>
                <span className="mr-auto text-purple-400 font-bold text-xl shrink-0">✓</span>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── BONUSES ── */}
      <section ref={bonusRef} className="py-16 px-6 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-3 text-center">אחרי הקורס הזה אתם תדעו בדיוק איזה כיוון מתאים לכם</h2>
          <p className="text-gray-500 text-center mb-10">מעבר ל-21 התפקידים, אתם תקבלו כלים פרקטיים להמשך הדרך</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bonuses.map((b, i) => (
              <div key={i} className="bonus-card bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow duration-200">
                <div className="text-4xl mb-4">{b.icon}</div>
                <h3 className="font-bold text-gray-800 mb-2 text-lg">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING STATEMENT ── */}
      <section ref={closingRef} className="py-20 px-6 bg-gray-900 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <p className="closing-line text-gray-400 text-sm uppercase tracking-widest mb-4">השורה התחתונה</p>
          <h2 className="closing-line text-3xl md:text-4xl font-extrabold mb-6 leading-tight">
            המטרה של הקורס הזה היא לא לתת לכם עוד מידע.
          </h2>
          <p className="closing-line text-xl text-white/70 mb-4">
            היא לתת לכם דבר אחד שחסר להרבה אנשים בשלב הזה:
          </p>
          <p className="closing-line text-4xl font-extrabold text-pink-400 mb-8">בהירות.</p>
          <p className="closing-line text-white/60 text-lg mb-10">
            כדי שתוכלו להפסיק לנחש ולהתחיל לבחור.
          </p>
          <a
            href="#pricing"
            className="closing-line inline-block px-12 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full text-xl hover:opacity-90 transition-opacity shadow-lg"
          >
            להצטרפות לקורס   ₪49
          </a>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" ref={pricingRef} className="py-20 px-6 bg-white">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-800 mb-2">מחיר הקורס</h2>
            <p className="text-gray-500">מחיר השקה מיוחד · לזמן מוגבל</p>
          </div>
          <div className="pricing-card bg-gradient-to-br from-indigo-700 via-purple-700 to-pink-600 rounded-3xl p-8 text-white shadow-2xl text-center">
            <div className="flex items-center justify-center gap-3 mb-1">
              <span className="text-2xl text-white/40 line-through">₪89</span>
              <span className="text-7xl font-extrabold">₪49</span>
            </div>
            <div className="text-sm bg-white/20 rounded-full px-4 py-1.5 inline-block mb-8 font-medium">
              🔥 מחיר השקה · גישה לכל התוכן
            </div>
            <ul className="space-y-3 text-right mb-10">
              {priceIncludes.map((item, i) => (
                <li key={i} className="flex items-center gap-2 justify-end text-white/90">
                  <span>{item}</span>
                  <span className="text-green-300 font-bold shrink-0">✓</span>
                </li>
              ))}
            </ul>

            {submitted ? (
              <div className="bg-white/15 border border-white/25 rounded-xl p-6 text-right">
                <p className="font-bold text-lg mb-2">תודה! 🎉</p>
                <p className="text-white/85 leading-relaxed">
                  שלחנו את הפרטים שלך. ניצור איתך קשר בהקדם עם אמצעי התשלום בביט   ולאחר התשלום תתקבל גישה לקורס לכתובת האימייל שהשארת.
                </p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="text-right space-y-3 mb-2">
                <input type="hidden" name="message" value="בקשת הצטרפות לקורס קריירה טכנולוגית — ₪49" />
                <div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="שם מלא"
                    dir="rtl"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                </div>
                <div>
                  <input
                    id="tel"
                    name="tel"
                    type="tel"
                    placeholder="טלפון"
                    dir="rtl"
                    value={formData.tel}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                </div>
                <div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="אימייל (לקבלת הגישה לקורס)"
                    dir="rtl"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                </div>
                {formError && <p className="text-amber-200 text-sm font-medium">{formError}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  className="block w-full py-4 bg-white text-purple-700 font-bold rounded-xl text-xl hover:bg-purple-50 transition-colors duration-200 shadow-lg disabled:opacity-60"
                >
                  {sending ? 'שולח...' : 'הרשמה עכשיו   ₪49'}
                </button>
                <p className="text-white/40 text-xs mt-3">
                  🔒 התשלום מתבצע בביט   נשלח לך את הפרטים באימייל לאחר ההרשמה
                </p>
              </form>
            )}
          </div>
          <p className="text-center text-gray-400 text-sm mt-6">
            אל תדלגו על הפרק האחרון בקורס   שם הטיפים הכי פרקטיים לקבלת החלטות קריירה נכונות.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoursePage;
